import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Linkedin, Instagram, Youtube, Plane } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { vehicleCategoriesAPI } from '../services/api';

const Footer = () => {
  const { t, language } = useLanguage();
  const [categories, setCategories] = useState([]);

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await vehicleCategoriesAPI.getAll();
        // Filter out 'all' category for footer
        setCategories((data || []).filter(cat => cat.id !== 'all'));
      } catch (err) {
        console.error('Failed to fetch categories:', err);
        setCategories([
          { id: 'iha-s1', name: 'UAV-S1', name_tr: 'İHA-S1' },
          { id: 'iha-s2', name: 'UAV-S2', name_tr: 'İHA-S2' },
          { id: 'prototype', name: 'Prototype', name_tr: 'Prototip' }
        ]);
      }
    };
    fetchCategories();
  }, []);

  // Get localized category name
  const getCategoryName = (cat) => {
    if (language === 'tr' && cat.name_tr) return cat.name_tr;
    return cat.name;
  };

  // Social media links
  const socialLinks = [
    { 
      name: 'LinkedIn', 
      url: 'https://www.linkedin.com/company/kartal-air/', 
      icon: Linkedin 
    },
    { 
      name: 'Instagram', 
      url: 'https://www.instagram.com/kartalairoffical?igshid=ODVuZW1oNmxjcmdy', 
      icon: Instagram 
    },
    { 
      name: 'YouTube', 
      url: 'https://www.youtube.com/@kartalairiha', 
      icon: Youtube 
    },
    { 
      name: 'Email', 
      url: 'mailto:kartalair.sponsorluk@gmail.com', 
      icon: Mail 
    }
  ];

  return (
    <footer style={{
      background: 'rgba(30, 41, 59, 0.6)',
      backdropFilter: 'blur(16px)',
      borderTop: '1px solid rgba(0, 240, 255, 0.2)'
    }} className="py-16">
      <div className="max-w-[1400px] mx-auto px-[7.6923%]">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* About */}
          <div>
            <img 
              src="https://customer-assets.emergentagent.com/job_aero-eagles/artifacts/2k3o5u10_KARTAL%20A%C4%B0R%20LOGO.png" 
              alt="Kartal Air" 
              className="h-10 object-contain mb-6"
              style={{
                filter: 'drop-shadow(0 0 8px rgba(0, 240, 255, 0.4))'
              }}
            />
            <p className="text-base text-[#F8FAFC] leading-relaxed opacity-85">
              {t('footer.description')}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-semibold mb-6" style={{ color: '#F8FAFC' }}>{t('footer.quickLinks')}</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-base text-[#94A3B8] hover:text-[#00F0FF] transition-colors duration-300">
                  {t('nav.home')}
                </Link>
              </li>
              <li>
                <Link to="/aircraft" className="text-base text-[#94A3B8] hover:text-[#00F0FF] transition-colors duration-300">
                  {t('nav.aircraft')}
                </Link>
              </li>
              <li>
                <Link to="/team" className="text-base text-[#94A3B8] hover:text-[#00F0FF] transition-colors duration-300">
                  {t('nav.team')}
                </Link>
              </li>
              <li>
                <Link to="/build-logs" className="text-base text-[#94A3B8] hover:text-[#00F0FF] transition-colors duration-300">
                  {t('nav.buildLogs')}
                </Link>
              </li>
              <li>
                <Link to="/achievements" className="text-base text-[#94A3B8] hover:text-[#00F0FF] transition-colors duration-300">
                  {t('nav.achievements')}
                </Link>
              </li>
              <li>
                <Link to="/sponsors" className="text-base text-[#94A3B8] hover:text-[#00F0FF] transition-colors duration-300">
                  {t('nav.sponsors')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Vehicle Categories - Dynamic */}
          <div>
            <h3 className="text-xl font-semibold mb-6" style={{ color: '#F8FAFC' }}>{t('nav.aircraft')}</h3>
            <ul className="space-y-3">
              {categories.map((cat) => (
                <li key={cat.id}>
                  <Link 
                    to={`/vehicles/${cat.id}`}
                    className="text-base text-[#94A3B8] hover:text-[#00F0FF] transition-colors duration-300 flex items-center gap-2"
                  >
                    <Plane size={14} style={{ color: '#00F0FF' }} />
                    {getCategoryName(cat)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact & Social */}
          <div>
            <h3 className="text-xl font-semibold mb-6" style={{ color: '#F8FAFC' }}>{t('footer.contact')}</h3>
            <ul className="space-y-4 mb-6">
              <li className="flex items-start gap-3">
                <MapPin size={20} className="mt-1 flex-shrink-0" style={{ color: '#00F0FF' }} />
                <span className="text-base text-[#F8FAFC] opacity-85">
                  {t('footer.schoolName')}
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={20} className="flex-shrink-0" style={{ color: '#00F0FF' }} />
                <a 
                  href="mailto:kartalair.sponsorluk@gmail.com" 
                  className="text-base text-[#F8FAFC] opacity-85 hover:text-[#00F0FF] transition-colors duration-300"
                >
                  kartalair.sponsorluk@gmail.com
                </a>
              </li>
            </ul>

            {/* Social Media */}
            <h4 className="text-lg font-semibold mb-4" style={{ color: '#F8FAFC' }}>{t('footer.followUs')}</h4>
            <div className="flex flex-wrap gap-3">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a 
                    key={social.name}
                    href={social.url}
                    target={social.url.startsWith('mailto:') ? '_self' : '_blank'}
                    rel="noopener noreferrer"
                    className="w-12 h-12 flex items-center justify-center text-white transition-all duration-300 rounded-lg"
                    style={{
                      background: 'rgba(30, 41, 59, 0.6)',
                      border: '1px solid rgba(0, 240, 255, 0.3)'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = '#00F0FF';
                      e.currentTarget.style.color = '#0F172A';
                      e.currentTarget.style.boxShadow = '0 0 20px rgba(0, 240, 255, 0.6)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = 'rgba(30, 41, 59, 0.6)';
                      e.currentTarget.style.color = 'white';
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                    aria-label={social.name}
                    data-testid={`footer-social-${social.name.toLowerCase()}`}
                  >
                    <Icon size={22} />
                  </a>
                );
              })}
            </div>
            
            {/* Sponsorship Button */}
            <a 
              href="mailto:kartalair.sponsorluk@gmail.com?subject=Sponsorluk%20Talebi"
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all duration-300"
              style={{
                background: 'linear-gradient(135deg, #00F0FF 0%, #0066FF 100%)',
                color: '#0F172A'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.boxShadow = '0 0 25px rgba(0, 240, 255, 0.5)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              data-testid="footer-sponsorship-btn"
            >
              <Mail size={18} />
              {t('footer.sponsorshipBtn')}
            </a>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 flex flex-col md:flex-row justify-between items-center gap-4" style={{
          borderTop: '1px solid rgba(0, 240, 255, 0.2)'
        }}>
          <p className="text-base text-[#94A3B8]">
            © 2025 Kartal Air. {t('footer.rights')}
          </p>
          <p className="text-base text-[#94A3B8]">
            {t('footer.competition')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
